import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { environment } from 'routes/environment.router';
import { LeadAttachmentService, MAX_ATTACHMENT_BYTES } from '../services/lead-attachment.service';

/// Envio de anexos do lead, igual para o clipe do cabeçalho e para o arquivo solto na lista.
export const useLeadAttachmentUpload = (leadId: string, onUploaded: () => void) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [sending, setSending] = useState(false);
    const enabled = environment.features.attachmentUpload;

    /// Desligado até o armazenamento definitivo: avisa em vez de abrir o seletor ou enviar.
    const warnUnavailable = useCallback(
        () => showAlert(t('general.emDesenvolvimento'), 'info', 2500),
        [showAlert, t]);

    const send = useCallback(async (list: FileList | null) => {
        if (!enabled) {
            warnUnavailable();
            return;
        }

        const chosen = Array.from(list ?? []);
        if (chosen.length === 0) return;

        const tooBig = chosen.filter((file) => file.size > MAX_ATTACHMENT_BYTES);

        if (tooBig.length > 0) {
            showAlert(t('telaLead.arquivoGrande', { nome: tooBig[0].name }), 'warning', 4000);
            return;
        }

        setSending(true);

        try {
            for (const file of chosen) await new LeadAttachmentService().upload(leadId, file);
            showAlert(t('telaLead.arquivoEnviado'), 'success', 2000);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSending(false);
            // Mesmo com erro no meio, o que subiu antes dele aparece.
            onUploaded();
        }
    }, [enabled, leadId, onUploaded, showAlert, t, warnUnavailable]);

    return { send, sending, enabled, warnUnavailable };
};
